import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSave, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

import { fetchTableFieldData, getTranslation, uploadFile } from "../../services/tablesService";
import { renderAsync } from "docx-preview";
import { API } from "../../api";
import { getAccessToken } from "../../services/authService";

// --------- react-pdf-viewer ---------
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

// --------- Стили для тулбара (Download, Zoom, Fullscreen и т.д.) ---------
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

/**
 * Универсальный модал для редактирования компонента Strapi.
 * Работает и с repeatable (массив) и с single (объект) компонентами.
 *
 * @param {boolean} show - Показывать ли модал
 * @param {function} handleClose - Закрыть модал
 * @param {string} endpoint - Например, "companies", используется в fetchTableFieldData
 * @param {number} id - ID родительской записи
 * @param {string} field - Название поля, где хранится компонент (например, "finansovye_pokazateli")
 * @param {function} onSave - Коллбэк для сохранения. Вызывается как onSave(recordId, fieldName, newValue)
 */
const ComponentModal = ({ show, handleClose, endpoint, id, field, onSave }) => {
  const [componentData, setComponentData] = useState(null); // Может быть массив (repeatable) или объект (single)
  const [schema, setSchema] = useState(null);
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Для предпросмотра файлов (PDF, DOCX)
  const [previewFile, setPreviewFile] = useState(null);
  // Индикатор загрузки файлов
  const [isUploading, setIsUploading] = useState(false);

  // Плагин для PDF-вьювера
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  /**
   * Вспомогательная функция: инициализируем поля
   * (если нет данных в компоненте, заполняем пустыми значениями)
   */
  const initializeComponentData = (localSchema) => {
    const init = {};
    for (const key in localSchema) {
      if (!Object.prototype.hasOwnProperty.call(localSchema, key)) continue;
      // Если поле - media и multiple, то пустой массив
      if (localSchema[key].type === 'media' && localSchema[key].multiple) {
        init[key] = [];
      } else {
        init[key] = '';
      }
    }
    return init;
  };

  /**
   * Загружаем схему + данные компонента
   */
  useEffect(() => {
    const loadComponentData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Запрашиваем { component, scheme } с бэкенда
        const response = await fetchTableFieldData(endpoint, id, field);
        if (!response || !response.scheme) {
          throw new Error("Invalid response structure");
        }

        // Сохраняем схему
        setSchema(response.scheme);

        // Проверяем, repeatable ли это поле (если Strapi 4+)
        const isRepeatable = !!response?.scheme?.__repeatable;
        // Однако многие варианты Strapi хранят repeatable флаг "глубже" или в другом месте.
        // Вы можете определять по факту: если в "component" массив, значит repeatable.
        // Либо, если schema[field] содержит { repeatable: true }.

        // Данные, пришедшие с бэка
        let incomingData = response.component;
        // Если пришёл массив (repeatable) - так и оставляем,
        // но если это реально пустой массив, то инициализируем newData = []
        // Если пришёл объект (single) - просто кладём в state объект

        // Однако Strapi может вернуть single компонент как объект,
        // repeatable - как массив (даже если 1 элемент).
        // Универсальная логика:
        if (Array.isArray(incomingData)) {
          // Значит repeatable
          if (incomingData.length === 0) {
            // Если пусто, инициализируем хотя бы одним объектом ИЛИ пустым массивом
            // Зависит от вашей бизнес-логики: создать новый элемент или нет?
            incomingData = [];
          }
          setComponentData(incomingData);
        } else {
          // Значит single (или null/undefined)
          if (!incomingData) {
            // Пусто => инициализируем значениями
            incomingData = initializeComponentData(response.scheme);
          }
          setComponentData(incomingData);
        }

        // Загружаем переводы для названий полей
        const newTranslations = {};
        await Promise.all(
          Object.keys(response.scheme).map(async (key) => {
            newTranslations[key] = await getTranslation(key);
          })
        );
        setTranslations(newTranslations);

      } catch (err) {
        console.error(err);
        setError(err.message || "Не удалось загрузить данные компонента");
      } finally {
        setLoading(false);
      }
    };

    if (show) {
      loadComponentData();
    }
  }, [show, endpoint, id, field]);

  /**
   * Предпросмотр .docx с помощью docx-preview
   */
  useEffect(() => {
    if (previewFile && previewFile.ext === '.docx') {
      const container = document.getElementById("docx-preview-container");
      if (container) {
        container.innerHTML = "";
        fetch(previewFile.uri)
          .then((res) => res.blob())
          .then((blob) => renderAsync(blob, container))
          .catch((err) => console.error("Ошибка при отображении .docx:", err));
      }
    }
  }, [previewFile]);

  /**
   * Узнаём, хранится ли у нас сейчас массив (repeatable) или объект (single).
   */
  const isRepeatableInState = Array.isArray(componentData);

  /**
   * Вспомогательная функция: изменить поле в single-компоненте
   */
  const setSingleFieldValue = (fieldKey, newValue) => {
    setComponentData(prev => ({
      ...prev,
      [fieldKey]: newValue,
    }));
  };

  /**
   * Вспомогательная функция: изменить поле у repeatable-компонента (в i-м элементе массива)
   */
  const setRepeatableFieldValue = (index, fieldKey, newValue) => {
    setComponentData(prev => {
      const newArr = [...prev];
      newArr[index] = {
        ...newArr[index],
        [fieldKey]: newValue
      };
      return newArr;
    });
  };

  /**
   * Обработка изменения "input"
   */
  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (isRepeatableInState) {
      setRepeatableFieldValue(index, name, value);
    } else {
      setSingleFieldValue(name, value);
    }
  };

  /**
   * Добавить новый объект в массив (только если repeatable)
   */
  const handleAddRepeatableItem = () => {
    if (!isRepeatableInState) return;
    // Инициализируем новый объект пустыми полями
    const newObj = initializeComponentData(schema);
    setComponentData(prev => [...prev, newObj]);
  };

  /**
   * Удалить объект из массива repeatable
   */
  const handleRemoveRepeatableItem = (idx) => {
    if (!isRepeatableInState) return;
    setComponentData(prev => prev.filter((_, i) => i !== idx));
  };

  /**
   * Загрузка файла (media)
   * index — индекс элемента (если repeatable), fieldKey — название поля
   */
  const handleFileChange = async (e, fieldKey, index) => {
    const files = Array.from(e.target.files);
    setIsUploading(true);

    try {
      const uploadedFiles = await Promise.all(files.map((file) => uploadFile(file)));

      // Если multiple, складываем в массив, иначе берём один файл
      const isMultiple = schema[fieldKey]?.multiple;
      let newValue;
      if (isMultiple) {
        // Текущее значение (может быть пустым массивом)
        const prevVal = isRepeatableInState
          ? componentData[index][fieldKey] || []
          : componentData[fieldKey] || [];

        newValue = [...prevVal, ...uploadedFiles];
      } else {
        newValue = uploadedFiles[0];
      }

      // Устанавливаем в state
      if (isRepeatableInState) {
        setRepeatableFieldValue(index, fieldKey, newValue);
      } else {
        setSingleFieldValue(fieldKey, newValue);
      }

      // При желании здесь можно сделать "частичное обновление" (PUT) одного поля
      // как в вашем примере updateSingleField(key, newValue).
      // Но ниже (handleSave) всё равно есть полное сохранение.
      //
      // Пример:
      // await updateSingleField(fieldKey, newValue);

    } catch (error) {
      console.error("Ошибка при загрузке файлов:", error);
      setError("Не удалось загрузить файлы");
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Удаление загруженного файла из массива (только если multiple = true)
   */
  const handleRemoveFile = (fieldKey, fileIndex, index) => {
    if (!schema[fieldKey]?.multiple) return;
    if (isRepeatableInState) {
      setComponentData(prev => {
        const newArr = [...prev];
        const filesArr = [...(newArr[index][fieldKey] || [])];
        filesArr.splice(fileIndex, 1);
        newArr[index] = { ...newArr[index], [fieldKey]: filesArr };
        return newArr;
      });
    } else {
      setComponentData(prev => {
        const filesArr = Array.isArray(prev[fieldKey]) ? [...prev[fieldKey]] : [];
        filesArr.splice(fileIndex, 1);
        return { ...prev, [fieldKey]: filesArr };
      });
    }
  };

  /**
   * Сохранение изменений (вся структура) через родительский onSave
   */
  const handleSaveAll = async () => {
    try {
      // Для Strapi repeatable нужно массив, single — объект
      // Если в `componentData` уже массив, значит repeatable, если объект — single.
      let finalValue = componentData;

      // Если single, а вдруг `componentData === null`, инициализируем пустым объектом
      if (!isRepeatableInState && componentData == null) {
        finalValue = {};
      }

      // Если у нас media поля, где multiple=true, в finalValue должны быть `[{id: ...}]`.
      // Ниже — простой пример: рекурсивно проходим поля и заменяем файлы.
      // Но можно сделать это локально в handleFileChange. Здесь — "универсальный" подход.

      const convertMediaToIds = (data) => {
        // Если data — массив (repeatable компонент):
        if (Array.isArray(data)) {
          return data.map((item) => convertMediaToIds(item));
        }
        // Если data — объект (single компонент)
        if (typeof data === 'object' && data !== null) {
          const newObj = { ...data };
          for (const key of Object.keys(newObj)) {
            if (schema[key]?.type === 'media') {
              // если multiple => массив {id:...}
              if (schema[key]?.multiple && Array.isArray(newObj[key])) {
                newObj[key] = newObj[key].map((fileObj) => ({ id: fileObj.id }));
              } else if (!schema[key]?.multiple && newObj[key] && newObj[key].id) {
                newObj[key] = { id: newObj[key].id };
              } else if (!schema[key]?.multiple && !newObj[key]) {
                newObj[key] = null;
              }
            } else if (typeof newObj[key] === 'object') {
              // рекурсивно проходим вложенности, если в вашем случае может быть вложенный компонент
              // (на усмотрение, если это реально нужно)
              // newObj[key] = convertMediaToIds(newObj[key]);
            }
          }
          return newObj;
        }
        // иначе возвращаем как есть
        return data;
      };

      // Подготовим payload:
      const payload = convertMediaToIds(finalValue);

      // Теперь вызываем родительский коллбэк
      // onSave( recordId, fieldName, payload )
      await onSave(id, field, payload);

      // Закрываем модалку
      handleClose();

    } catch (err) {
      console.error("Ошибка при сохранении данных компонента:", err);
      setError("Не удалось сохранить изменения");
    }
  };

  /**
   * Отрисовка поля по типу (string, integer, date, enumeration, media).
   * Если у нас repeatable, index != null => рендерим элемент массива
   * Если single, index == null => рендерим из объекта
   */
  const renderField = (key, index = null) => {
    if (!schema || !schema[key]) return null;
    const fieldType = schema[key].type;
    const label = translations[key] || key;

    // Определяем текущее значение поля
    let value;
    if (isRepeatableInState && index !== null) {
      value = componentData[index]?.[key];
    } else if (!isRepeatableInState) {
      value = componentData?.[key];
    }

    switch (fieldType) {
      case 'string':
      case 'text':
      case 'integer':
        return (
          <Form.Group key={key}>
            <Form.Label>{label}</Form.Label>
            <Form.Control
              placeholder="Введите данные"
              type={fieldType === 'integer' ? 'number' : 'text'}
              name={key}
              value={value || ''}
              onChange={(e) => handleInputChange(e, index)}
            />
          </Form.Group>
        );

      case 'date':
        return (
          <Form.Group key={key}>
            <Form.Label>{label}</Form.Label>
            <Form.Control
              type="date"
              name={key}
              value={value || ''}
              onChange={(e) => handleInputChange(e, index)}
            />
          </Form.Group>
        );

      case 'enumeration':
        return (
          <Form.Group key={key}>
            <Form.Label>{label}</Form.Label>
            <Form.Select
              name={key}
              value={value || ''}
              onChange={(e) => handleInputChange(e, index)}
            >
              <option value="">Выберите...</option>
              {schema[key].enum?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        );

      case 'media':
        // Если multiple => value массив. Если нет => объект / null
        const isMultiple = schema[key].multiple;
        let mediaFiles = [];
        if (isMultiple) {
          mediaFiles = Array.isArray(value) ? value : [];
        } else {
          mediaFiles = value ? [value] : [];
        }

        return (
          <Form.Group key={key}>
            <Form.Label>{label}</Form.Label>
            <Form.Control
              type="file"
              name={key}
              multiple={isMultiple}
              onChange={(e) => handleFileChange(e, key, index)}
            />
            <Button
              variant="outline-primary"
              className="mt-2 d-flex align-items-center"
              onClick={() => {
                // Клик по скрытому input, но у нас выше Form.Control
                // Можно вызвать e.target.files...
                const input = document.getElementsByName(key);
                if (input && input.length > 0) input[0].click();
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Добавить файл
              {isUploading && <Spinner animation="border" size="sm" className="ms-2" />}
            </Button>

            {/* Список уже загруженных файлов */}
            {mediaFiles.length > 0 && (
              <div className="mt-3">
                {mediaFiles.map((file, fIdx) => {
                  const fileUrl = file.url
                    ? (file.url.startsWith('http') ? file.url : API.BASE_URL.replace(/\/api$/, "") + file.url)
                    : null;

                  return (
                    <div key={fIdx} className="d-flex align-items-center mb-2">
                      {fileUrl ? (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setPreviewFile({ uri: fileUrl, ext: file.ext });
                          }}
                          className="me-2"
                        >
                          {file.name || `Файл ${fIdx + 1}`}
                        </a>
                      ) : (
                        <span className="me-2">{file.name || `Файл ${fIdx + 1}`}</span>
                      )}

                      {isMultiple && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveFile(key, fIdx, index)}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Удалить
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Form.Group>
        );

      default:
        return null;
    }
  };

  // Если модал не показывается — ничего не рендерим
  if (!show) return null;

  // Состояние "загружаем данные" или "ошибка"
  if (loading) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" />
        </Modal.Body>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body className="text-danger">
          Произошла ошибка: {error}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  /**
   * Основной рендер форм.
   * Если isRepeatableInState === true => создаём .map() на каждую "подзапись"
   * Если false => просто 1 "подзапись"
   */
  const renderFormFields = () => {
    if (!schema || !componentData) return null;

    if (isRepeatableInState) {
      // Массив
      return componentData.map((_, index) => (
        <div key={index} className="p-3 mb-3" style={{ border: '1px solid #eee' }}>
          {/* Выводим все ключи из schema */}
          {Object.keys(schema).map((key) => (
            <Form.Group className="mb-3" key={`${key}-${index}`}>
              {renderField(key, index)}
            </Form.Group>
          ))}
          {/* Кнопка удаления этой "подзаписи" */}
          <Button
            variant="outline-danger"
            className="mt-2"
            onClick={() => handleRemoveRepeatableItem(index)}
          >
            Удалить элемент <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      ));
    } else {
      // Объект (single)
      return (
        <>
          {Object.keys(schema).map((key) => (
            <Form.Group className="mb-3" key={key}>
              {renderField(key, null)}
            </Form.Group>
          ))}
        </>
      );
    }
  };

  return (
    <>
      {/* Основная модалка для редактирования */}
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Редактировать {isRepeatableInState ? 'повторяющийся' : 'одиночный'} компонент
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {renderFormFields()}

            {isRepeatableInState && (
              <Button variant="outline-primary" onClick={handleAddRepeatableItem}>
                <FontAwesomeIcon icon={faPlus} /> Добавить элемент
              </Button>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Закрыть
          </Button>
          <Button variant="primary" onClick={handleSaveAll}>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Сохранить изменения
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно для предпросмотра файлов */}
      <Modal show={Boolean(previewFile)} onHide={() => setPreviewFile(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Предпросмотр файла</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: '80vh' }}>
          {previewFile && previewFile.ext === '.docx' ? (
            // Просмотр .docx через docx-preview
            <div
              id="docx-preview-container"
              style={{ width: '100%', height: '100%', overflow: 'auto' }}
            />
          ) : previewFile && previewFile.ext === '.pdf' ? (
            // PDF через react-pdf-viewer
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.7.107/build/pdf.worker.min.js">
              <div style={{ height: '100%', width: '100%' }}>
                <Viewer fileUrl={previewFile.uri} plugins={[defaultLayoutPluginInstance]} />
              </div>
            </Worker>
          ) : (
            <div>Формат файла не поддерживается предварительным просмотром</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPreviewFile(null)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ComponentModal;
